import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { trackClientEvent } from 'analytics/analytics'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import {
  arePropertyRatesLoading,
  getPropertyCheapestRates,
  getPropertyRatesKey,
} from 'selectors/offerPage/offerPageSelectors'

import PropertyCheapestRatePriceDetails from './PropertyCheapestRatePriceDetails'

const SummaryPane = styled(Pane)`
  position: relative;
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  lpcOffer: App.HotelOffer
  areAnytimeRates: boolean
  shouldShowAnytimeRate: boolean
  isLoading: boolean
  cheapestPropertyRates: [App.EnrichedPropertyRate | App.Property.AnytimeRate | undefined, App.EnrichedPropertyRate | undefined]
  searchId: string | undefined
  /** TODO for styled media queries */
  className?: string
  onViewPackageOptions: () => void
}

function PropertySummary({
  property,
  lpcOffer,
  areAnytimeRates,
  shouldShowAnytimeRate,
  isLoading,
  cheapestPropertyRates,
  searchId,
  className,
  onViewPackageOptions,
}: Props) {
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const onSignUpOrLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const [hotelOnlyRate, hotelWithFlightsRate] = cheapestPropertyRates

  const hasTrackedPrice = useRef(false)
  const hotelOnlyDisplayPrice = hotelOnlyRate?.totals.displayPrice
  useEffect(() => {
    if (searchId && !isLoading && !hasTrackedPrice.current) {
      if (hotelOnlyDisplayPrice) {
        trackClientEvent({
          subject: `searchId:${searchId}_offerId:${lpcOffer.id}`,
          action: hotelOnlyDisplayPrice.toString(),
          category: 'price_accuracy',
          type: 'operational',
        })
      }
      // If we're not loading and there's no cheapest price, it's likely that we don't have dates
      // set (i.e. we arrived via an anytime search result). Since we're not displaying initial
      // rates, we must refrain from firing the pricing accuracy event when a cheapest rate _does_
      // become available.
      //
      // For the case where dates _are_ set, we're relying on clean transitions (i.e. no gaps due to
      // races) from `isLoading` being set to `hotelOnlyRate` being available.
      hasTrackedPrice.current = true
    }
  }, [searchId, isLoading, hotelOnlyDisplayPrice, lpcOffer])

  const hideAnytimePrices = areAnytimeRates && !shouldShowAnytimeRate
  const onCta = useCallback(() => {
    if (hideAnytimePrices) {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else {
      onViewPackageOptions()
    }
  }, [hideAnytimePrices, dispatch, onViewPackageOptions])

  const ctaText = hideAnytimePrices
    ? intl.formatMessage({
        id: 'RuM40FaLnPgzb1r61ORb',
        defaultMessage: 'Select dates',
        description: 'CTA text when dates need to be selected on property summary',
      })
    : intl.formatMessage({
        id: 'IwfaJyEAJ7wPj6tL7uSA',
        defaultMessage: 'View package options',
        description: 'CTA text to view package options on property summary',
      })

  return <SummaryPane border="light-foreground" borderRadius="S" padding="28 20" shadow="bottom-small" className={className}>
    <WalledContent
      enforced={property.walledGarden}
      wall={
        <Group direction="vertical" gap={16}>
          {/* The following is based on `<GenericLoginSignupWall/>`, used by `<PropertyStickyHeader/>`. */}
          <BodyTextBlock
            variant="medium"
            weight="bold"
            startIcon={<LineInfoCircleIcon />}
          >
            {intl.formatMessage({
              id: 'w-jCEfeoOuAa6oxdoAmk',
              defaultMessage: 'Join now to view member prices',
              description: 'Wall message for non-members on the property summary',
            })}
          </BodyTextBlock>
          <Group direction="vertical" gap={8}>
            <TextButton kind="primary" onClick={onSignUpOrLoginClick}>
              {intl.formatMessage({
                id: 'jriEHMIML1WtgYOIfQng',
                defaultMessage: 'Sign up for free',
                description: 'Sign up button text on property summary on the login or signup page',
              })}
            </TextButton>
            <TextButton kind="secondary" onClick={onSignUpOrLoginClick}>
              {intl.formatMessage({
                id: 'D_4hSdWDoUx1sMPc11fp',
                defaultMessage: 'Log in',
                description: 'Login button text on property summary on the login or signup page',
              })}
            </TextButton>
          </Group>
        </Group>
      }
    >
      <Group direction="vertical" gap={12}>
        {!hideAnytimePrices && <>
          {hotelOnlyRate && <PropertyCheapestRatePriceDetails
            hotelOnlyRate={hotelOnlyRate}
            hotelWithFlightsRate={hotelWithFlightsRate}
            horizontalAlign="center"
          />}
          {isLoading && <Group direction="vertical" horizontalAlign="center">
            <LoadingIndicator floating visible opaque backdrop="white" />
          </Group>}
        </>}
        {hideAnytimePrices && <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineInfoCircleIcon />}
        >
          {intl.formatMessage({
            id: 'OXRZ6EfZFR8Pi9Z9xOmg',
            defaultMessage: 'Select dates to get the best price for this offer',
            description: 'Info message when dates aren’t selected on property summary',
          })}
        </BodyTextBlock>}
        <TextButton onClick={onCta} kind="primary" fit="flex">
          {ctaText}
        </TextButton>
      </Group>
    </WalledContent>
  </SummaryPane>
}

export default withOfferPageAndReduxState((state, appState) => ({
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  // We only need to show anytime pricing for traffic coming from marketing, which will have
  // `utm_bp` set.
  shouldShowAnytimeRate: !!appState.utm.bp,
  isLoading: arePropertyRatesLoading(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  searchId: appState.router.location.query.searchId,
  open: !!state.activeFilter,
}), PropertySummary, preventRenderWithOpenFilter)
